import Guid from "common/values/guid/guid";
import Document from "documents/entities/document/document";
import DocumentName from "documents/values/document-name";
import DocumentTopic, { DocumentTopicJSON } from "documents/values/document-topic";
import {DocumentType} from "documents/values/document-type";
import moment, {Moment} from "moment";

export interface WorkDocumentJSON {
  id: string;
  name?: string;
  creatorId: string | null;
  fileSize: number;
  fileType: string;
  created?: string;
  readAccess?: string[];
  writeAccess?: string[];
  topics: DocumentTopicJSON[];
  templateIds: string[];
  hasSystemPermissions: boolean;
  documentType: WorkDocumentType;
  replacesId?: string;
}

export enum WorkDocumentType {
  Conflicts = "Conflicts",
  ClientPolicy = "ClientPolicy",
  VendorPolicy = "VendorPolicy"
}

export default class WorkDocument extends Document {
  public id: Guid;
  public replacesId?: Guid;
  public documentType: WorkDocumentType;

  constructor(
    id: Guid,
    name: DocumentName | undefined,
    creatorId: Guid | null,
    fileSize: number,
    fileType: DocumentType,
    created: Moment | undefined,
    readAccess: Guid[] | undefined,
    writeAccess: Guid[] | undefined,
    topics: DocumentTopic[],
    templateIds: Guid[],
    hasSystemPermissions: boolean,
    documentType: WorkDocumentType,
    replacesId?: Guid
  ) {
    super(
      id,
      name,
      creatorId,
      fileSize,
      fileType,
      created,
      readAccess,
      writeAccess,
      topics,
      templateIds,
      hasSystemPermissions
    );
    this.id = id;
    this.replacesId = replacesId;
    this.documentType = documentType;
  }

  public get isEmpty(): boolean {
    return false;
  }

  public static fromJSON(json: WorkDocumentJSON): WorkDocument {
    if (!json.id) throw new Error("Document Id is required");
    const id = Guid.fromJSON(json.id);
    const creatorId = json.creatorId ? Guid.fromJSON(json.creatorId): null;
    const name = json.name ? new DocumentName(json.name) : undefined;
    const fileSize = json.fileSize;
    const fileType = json.fileType as DocumentType;
    const created = json.created ? moment(json.created) : undefined;
    const readAccess = json.readAccess?.map((id) => Guid.fromJSON(id));
    const writeAccess = json.writeAccess?.map((id) => Guid.fromJSON(id));
    const topics = json.topics?.map((topic) => DocumentTopic.fromJSON(topic));
    const templateIds = json.templateIds?.map((id) => Guid.fromJSON(id)) ?? [];
    const hasSystemPermissions: boolean = Boolean(json.hasSystemPermissions);
    const replacesId = json.replacesId ? Guid.fromJSON(json.replacesId) : undefined;

    if (!id) throw new Error("Document Id is required");

    return new WorkDocument(
      id,
      name,
      creatorId,
      fileSize,
      fileType,
      created,
      readAccess,
      writeAccess,
      topics,
      templateIds,
      hasSystemPermissions,
      json.documentType,
      replacesId
    );
  }

  public static fromDocument(document: Document, documentType: WorkDocumentType, replacesId?: Guid) {
    return new WorkDocument(
      document.id,
      document.name,
      document.creatorId,
      document.fileSize,
      document.fileType,
      document.created,
      document.readAccess,
      document.writeAccess,
      document.topics,
      document.templateIds,
      document.hasSystemPermissions,
      documentType,
      replacesId
    );
  }

  public clone(): WorkDocument {
    return new WorkDocument(
      this.id,
      this.name,
      this.creatorId,
      this.fileSize,
      this.fileType,
      this.created,
      this.readAccess,
      this.writeAccess,
      this.topics,
      this.templateIds,
      this.hasSystemPermissions,
      this.documentType,
      this.replacesId
    );
  }

  public toJSON(): WorkDocumentJSON {
    const superJson = super.toJSON();
    return {
      ...superJson,
      creatorId: superJson.creatorId ?? null,
      hasSystemPermissions: Boolean(superJson.hasSystemPermissions),
      documentType: this.documentType,
      replacesId: this.replacesId?.toString()
    };
  }

  public isEqualTo(document: WorkDocument | null): boolean {
    return Boolean(document && this.id.isEqualTo(document.id));
  }

  public static get Prototype(): WorkDocument {
    return new WorkDocument(
      Guid.generate(),
      undefined,
      Guid.generate(),
      0,
      DocumentType.PDF,
      moment(),
      [],
      [],
      [],
      [],
      false,
      WorkDocumentType.ClientPolicy
    );
  }

  public toString(): string {
    return this.name?.toString() ?? "Untitled Document";
  }
}
